const isDev = () => location.href.includes('umucdn');
import { getCurrentLang, lang } from '@umu/ukit-lang';
import Toast from 'common/src/services/toast/toast';
import { TENCENT_CAPTCHA_ERROR_CODE } from './enum';
import { reportError } from 'common/src/services/errorLogger';

export const CAPTCHA_WAY = {
    INVISIBLE: 'invisible',
    VISIBLE: 'visible',
};

const TENCENT_JS = 'https://turing.captcha.qcloud.com/TJCaptcha.js';

const INTERNAL_TOKEN = 'recaptcha_umu_';

const CONFIG_CACHE: Record<string, any> = {};

interface LangMap {
    [key: string]: string;
}

/**
 * 根据TLang格式获取Google验证码对应的语言代码
 * @returns Google验证码语言代码
 */
function getGoogleLang(): string {
    // 如果没有传入tlang，则获取当前语言
    const lang = getCurrentLang();

    // TLang格式映射
    const tLangMap: LangMap = {
        cn: 'zh-CN',
        tw: 'zh-TW',
        en: 'en',
        jp: 'ja',
        ko: 'ko',
        fr: 'fr',
        es: 'es',
        de: 'de',
        it: 'it',
        pt: 'pt',
        ru: 'ru',
        th: 'th',
        vi: 'vi',
        id: 'id',
        sv: 'sv',
        nl: 'nl',
        ar: 'ar',
        he: 'iw', // Google使用iw作为希伯来语代码
    };

    // 先尝试TLang格式，如果不存在则尝试常规格式
    return tLangMap[lang] || 'en';
}

/**
 * 根据TLang格式获取腾讯验证码对应的语言代码
 * @returns 腾讯验证码语言代码
 */
function getTencentLang(): string {
    // 如果没有传入tlang，则获取当前语言
    const lang = getCurrentLang();

    // TLang格式映射
    const tLangMap: LangMap = {
        cn: 'zh-cn',
        tw: 'zh-tw',
        en: 'en',
    };

    return tLangMap[lang] || 'zh-cn';
}

interface GoogleCaptchaResult {
    provider: 'google';
    key_id: string;
    token: string;
    action: string;
    ja4: string;
    ja3: string;
}

interface TencentCaptchaResult {
    provider: 'tencent';
    ticket: string;
    randstr: string;
    captcha_app_id: number;
    action: string;
}

interface TencentCaptchaResponse {
    ret: number;
    ticket?: string;
    randstr?: string;
}

type CaptchaResult = GoogleCaptchaResult | TencentCaptchaResult;

interface CaptchaConfig {
    recaptcha_switch: number;
    provider: 'google' | 'tencent';
    google?: {
        visible: string;
        invisible: string;
    };
    tencent?: {
        visible: string;
        invisible: string;
    };
}

// 为window声明腾讯和谷歌验证码的类型
declare global {
    interface Window {
        grecaptcha: any;
        grecaptchaApi?: boolean;
        TencentCaptcha?: any;
        TJCaptcha?: any;
    }
}

class CaptchaManager {
    private config: CaptchaConfig;
    private captchaInfo: CaptchaResult;
    private isErrorCaptcha: boolean;
    private widgetId: number | null;
    private virtualContainer: HTMLElement | null;

    constructor() {
        this.config = {} as CaptchaConfig;
        this.captchaInfo = {} as CaptchaResult;
        this.isErrorCaptcha = false;
        this.widgetId = null;
        this.virtualContainer = null;
    }

    async getConfig(action: string): Promise<CaptchaConfig> {
        if (!isDev() && CONFIG_CACHE[action]) {
            return CONFIG_CACHE[action];
        }

        let API = `/uapi/v1/recaptcha/config?action=${action}`;
        if (isDev() && (window as any).captchaType === 'google') {
            API += '&type=google';
        }
        const resp = await fetch(API);
        const result = await resp.json();
        CONFIG_CACHE[action] = result.data;
        return result.data;
    }

    getIsErrorCaptcha(): boolean {
        return this.isErrorCaptcha;
    }

    // 核心方法：只负责验证，不调用业务API
    async getCaptchaInfo(action: string, containerId: string, isVisible: boolean = false): Promise<string> {
        this.config = await this.getConfig(action);

        if (isDev()) {
            console.log('getCaptchaInfo', action, containerId, isVisible);
        }

        if (isDev() && (window as any).debugCaptcha === 'visible') {
            isVisible = true;
        }

        if (this.config.recaptcha_switch === 1) {
            // 跳过
            return 'web_skip';
        }

        try {
            if (this.config.provider === 'tencent') {
                const tencentConfig = this.config.tencent!;
                const captchaAppId = isVisible ? tencentConfig.visible : tencentConfig.invisible;

                try {
                    const result = await this.captchaByTencent(captchaAppId, action, isVisible);
                    this.captchaInfo = result;
                } catch (e: any) {
                    reportError({
                        error: e,
                        componentName: 'CaptchaManager',
                        additionalInfo: { desc: 'Tencent verification failed' },
                    });
                    this.isErrorCaptcha = true;

                    if (e.error === TENCENT_CAPTCHA_ERROR_CODE.USER_CLOSE) {
                        throw e;
                    }

                    this.captchaInfo = {
                        ticket: `${INTERNAL_TOKEN}_web_${captchaAppId}`,
                        provider: 'tencent',
                        randstr: '',
                        captcha_app_id: Number(captchaAppId),
                        action: action,
                    };
                }
            } else {
                const googleConfig = this.config.google!;
                const googleKey = isVisible ? googleConfig.visible : googleConfig.invisible;

                try {
                    const result = await this.captchaByGoogle(googleKey, action, isVisible, containerId);
                    this.captchaInfo = result;
                } catch (e) {
                    reportError({
                        error: e,
                        componentName: 'CaptchaManager',
                        additionalInfo: { desc: 'Google verification failed' },
                    });
                    this.isErrorCaptcha = true;
                    this.captchaInfo = {
                        provider: 'google',
                        key_id: googleKey,
                        token: `${INTERNAL_TOKEN}_web_${googleKey}`,
                        action: action,
                        ja4: '',
                        ja3: '',
                    };
                }
            }

            return JSON.stringify(this.captchaInfo);
        } catch (error) {
            // 记录错误但不处理业务逻辑
            reportError({
                error: error,
                componentName: 'CaptchaManager',
                additionalInfo: { desc: 'Overall verification failed' },
            });
            throw error;
        }
    }

    async captchaByGoogle(
        googleKey: string,
        action: string,
        isVisible: boolean,
        containerId: string,
    ): Promise<GoogleCaptchaResult> {
        if (isVisible) {
            await this.loadGoogleCaptchaScriptVisible();
            return this.captchaByGoogleVisible(googleKey, action, containerId);
        } else {
            await this.loadGoogleCaptchaScript(googleKey);
            return this.captchaByGoogleInvisible(googleKey, action);
        }
    }

    private createOverlay() {
        const overlay = document.createElement('div');
        overlay.className = 'recaptcha-overlay';
        overlay.style.background = 'rgba(0, 0, 0, 0.6)';
        overlay.style.pointerEvents = 'none';
        overlay.style.position = 'fixed';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.width = '100%';
        overlay.style.height = '100%';
        overlay.style.zIndex = '10000';
        document.body.appendChild(overlay);
        return overlay;
    }

    async captchaByGoogleInvisible(googleKey: string, action: string): Promise<GoogleCaptchaResult> {
        const token = await window.grecaptcha.enterprise.execute(googleKey, {
            action,
        });

        return {
            provider: 'google',
            key_id: googleKey,
            token,
            action,
            ja4: '',
            ja3: '',
        };
    }

    async captchaByGoogleVisible(googleKey: string, action: string, containerId: string): Promise<GoogleCaptchaResult> {
        return new Promise<GoogleCaptchaResult>((resolve, _reject) => {
            const virtualContainer = this.createVirtualContainer(containerId);
            this.widgetId = window.grecaptcha.enterprise.render(virtualContainer, {
                action,
                sitekey: googleKey,
                theme: 'light',
                callback: (token: string) => {
                    resolve({
                        provider: 'google',
                        key_id: googleKey,
                        token,
                        action,
                        ja4: '',
                        ja3: '',
                    });
                },
            });
        });
    }

    createVirtualContainer(containerId: string): HTMLElement {
        const virtualContainer = document.createElement('div');
        virtualContainer.id = 'recaptcha-container-' + Math.random().toString(36).substring(2, 15);
        virtualContainer.className = 'recaptcha-container';
        virtualContainer.style['marginTop'] = '12px';
        virtualContainer.style['marginBottom'] = '12px';
        document.getElementById(containerId)!.appendChild(virtualContainer);

        this.virtualContainer = virtualContainer;
        return virtualContainer;
    }

    async captchaByTencent(captchaAppId: string, action: string, isVisible: boolean): Promise<TencentCaptchaResult> {
        await this.loadTencentCaptchaScript();
        let overlay: HTMLElement | null = null;
        return new Promise<TencentCaptchaResult>((resolve, reject) => {
            const captcha = new (window.TencentCaptcha as any)(
                String(captchaAppId),
                (res: TencentCaptchaResponse) => {
                    isDev() && console.log('验证结果:', res);
                    if (overlay) {
                        overlay.remove();
                    }

                    if (res.ret === 0) {
                        // 验证成功，发送票据到服务器验证
                        resolve({
                            provider: 'tencent',
                            ticket: res.ticket!,
                            randstr: res.randstr!,
                            captcha_app_id: Number(captchaAppId),
                            action: action,
                        });
                    } else if (res.ret === 2) {
                        // 用户关闭验证码
                        reject({
                            error: TENCENT_CAPTCHA_ERROR_CODE.USER_CLOSE,
                            ret: res.ret,
                        });
                    } else {
                        reportError({
                            error: res,
                            componentName: 'CaptchaManager',
                            additionalInfo: { desc: 'Tencent verification response error' },
                        });
                        reject({
                            error: '验证失败',
                            ret: res.ret,
                        });
                    }
                },
                {
                    userLanguage: getTencentLang(),
                    loading: false,
                },
            );

            if (isVisible) {
                overlay = this.createOverlay();
            }

            // 触发验证
            captcha.show();
        });
    }

    async loadGoogleCaptchaScript(googleKey: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            // 检查是否已加载
            if (window.grecaptcha || window.grecaptchaApi) {
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.src = `https://www.recaptcha.net/recaptcha/enterprise.js?render=${googleKey}&hl=${getGoogleLang()}`;
            script.async = true;
            script.onload = () => {
                window.grecaptcha.enterprise.ready(() => {
                    if (isDev()) {
                        console.log('Google验证码JS加载成功');
                    }
                    resolve();
                });
            };
            script.onerror = () => reject(new Error('Google验证码JS加载失败'));
            document.head.appendChild(script);
        });
    }

    async loadGoogleCaptchaScriptVisible(): Promise<void> {
        let isLoad = false;
        return new Promise<void>((resolve, reject) => {
            // 检查是否已加载
            if (window.grecaptcha || window.grecaptchaApi) {
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.src = `https://www.recaptcha.net/recaptcha/enterprise.js?render=explicit&hl=${getGoogleLang()}`;
            script.async = true;
            script.onload = () => {
                window.grecaptcha.enterprise.ready(() => {
                    if (isDev()) {
                        console.log('Google验证码JS加载成功');
                    }
                    if (!isLoad) {
                        resolve();
                    }
                    isLoad = true;
                });
                setTimeout(() => {
                    if (!isLoad) {
                        Toast.show(lang('无法获得验证码，请咨询公司IT。'));
                        reject(new Error('Google验证码JS加载失败'));
                    }
                    isLoad = true;
                }, 10000);
            };
            script.onerror = () => {
                Toast.show(lang('无法获得验证码，请咨询公司IT。'));
                reject(new Error('Google验证码JS加载失败'));
            };
            document.head.appendChild(script);
        });
    }

    async loadTencentCaptchaScript(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            // 检查是否已加载
            if (window.TencentCaptcha || window.TJCaptcha) {
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.src = TENCENT_JS;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => {
                reportError({
                    error: 'Tencent captcha JS load failed',
                    componentName: 'CaptchaManager',
                    additionalInfo: { jsUrl: TENCENT_JS },
                });
                Toast.show(lang('无法获得验证码，请咨询公司IT。'));
                reject(new Error('腾讯验证码JS加载失败'));
            };
            document.head.appendChild(script);
        });
    }

    reset(): void {
        if (this.widgetId) {
            window.grecaptcha.enterprise.reset(this.widgetId);
            this.widgetId = null;
        }

        if (this.virtualContainer) {
            this.virtualContainer.remove();
            this.virtualContainer = null;
        }
    }
}

export default CaptchaManager;
